'use client';

import { useEffect } from 'react';
import useCustomRouter from '@/hooks/useCustomRouter';
import usePurchaseLists from '@/lib/hooks/usePurchaseLists';
import useRequireMaster from '@/lib/hooks/useRequireMaster';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { DashboardLinks } from '@/components/pages/dashboard/constants';
import LoadingIcon from '@/components/atoms/loading-icon';
import Dashboard from '@/components/pages/dashboard';
import PurchaseListDetailPage from '@/components/pages/dashboard/pages/purchase-list-detail';

interface PurchaseListsClientWrapperProps {
  browseProductsHref: string;
}

const PurchaseListsClientWrapper = ({ browseProductsHref }: PurchaseListsClientWrapperProps) => {
  const router = useCustomRouter();
  // EWM-298 (OQ-2) — Master-only route. View-Only users are redirected to the
  // account dashboard; guests are bounced to sign-in by the route auth-gate.
  const access = useRequireMaster();
  const { selectedStore } = useStoreAndBusinessUnits();
  const { purchaseLists, isLoading, createPurchaseList } = usePurchaseLists(selectedStore?.key);

  const firstList = purchaseLists?.items?.[0];

  useEffect(() => {
    // Hold the open-first-list redirect until access is confirmed, so a blocked
    // View-Only user isn't raced into a list detail before the guard redirects.
    if (access === 'allowed' && !isLoading && firstList?.wishlistId) {
      router.replace(DashboardLinks.shoppingListDetail(firstList.wishlistId as string));
    }
  }, [access, isLoading, firstList, router]);

  // EWM-553 — while a redirect to the first list is pending, keep showing the
  // loader. The redirect runs in an effect (post-paint), so without this the
  // empty state would flash for one render when isLoading flips to false.
  if (access !== 'allowed' || isLoading || firstList?.wishlistId) {
    return (
      <Dashboard>
        <span className="flex w-full justify-center py-8">
          <LoadingIcon svgWidth={20} svgHeight={20} className="fill-gray-700" />
        </span>
      </Dashboard>
    );
  }

  // No lists — show empty state via the detail page
  return (
    <Dashboard>
      <PurchaseListDetailPage
        purchaseList={null}
        allLists={[]}
        browseProductsHref={browseProductsHref}
        onCreateList={async (name, description) => {
          if (!selectedStore) return false;
          const res = await createPurchaseList({ name, description, store: selectedStore });
          return !!res?.wishlistId;
        }}
      />
    </Dashboard>
  );
};

export default PurchaseListsClientWrapper;
